import {config} from '@/core/config';
import {
  UnauthorizedError,
  ForbiddenError,
  TooManyRequestsError,
  ServerError,
} from './errors';
import {handleApiError} from './errorHandler';

function buildApiUrl(path: string): string {
  const base = config.VITE_API_URL.replace(/\/$/, '');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalized}`;
}

export async function apiCall<T>(
  path: string,
  params: Record<string, unknown> = {},
): Promise<T> {
  const response = await fetch(buildApiUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (response.status === 401) {
    const error = new UnauthorizedError();
    handleApiError(error);
    throw error;
  }

  if (response.status === 403) {
    throw new ForbiddenError();
  }

  // 429 (Too Many Requests) is emitted by infrastructure rate limiting, not our
  // Node backend. The body may be non-JSON (proxy HTML), so we don't parse it;
  // the message is static. Rate limits reset after a short time.
  if (response.status === 429) {
    throw new TooManyRequestsError();
  }

  if (!response.ok) {
    throw new ServerError(
      response.status,
      `${response.status} ${response.statusText}`,
    );
  }

  const {result, error} = await response.json();

  if (error) {
    throw error;
  }

  return result;
}
